import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Send, Image, User, Camera } from 'lucide-react';
import authAPI from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Import CSS
import './dashboardComponents/aichat.css';
import aiAvatar from '../../assets/bot.png';
import SimpleMarkdownDisplay from '../SimpleMarkdownDisplay.jsx';

export default function AIChat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('english');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [activeSessionId, setActiveSessionId] = useState(null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);       // Gallery
  const cameraInputRef = useRef(null);     // Camera
  const messageInputRef = useRef(null);

  const languages = [
    { id: 'english', name: 'English' },
    { id: 'french', name: 'Français' },
    { id: 'igbo', name: 'Igbo' },
    { id: 'hausa', name: 'Hausa' },
    { id: 'yoruba', name: 'Yoruba' },
    { id: 'pidgin', name: 'Pidgin' }
  ];

  const fetchMessages = useCallback(async () => {
    try {
      setIsInitialLoading(true);
      const response = await authAPI.getAiHistory();

      if (response.success && response.data?.sessions) {
        const historyMessages = response.data.sessions.flatMap(session =>
          (session.messages || []).map(msg => ({
            ...msg,
            session_id: session.session_id
          }))
        );

        if (historyMessages.length > 0) {
          const sortedMessages = historyMessages.sort((a, b) =>
            new Date(a.created_at) - new Date(b.created_at)
          );

          setMessages(sortedMessages);

          const lastMsg = sortedMessages[sortedMessages.length - 1];
          setActiveSessionId(lastMsg.session_id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch AI history:', error);
    } finally {
      setIsInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('ai_language');
    if (savedLanguage) {
      setSelectedLanguage(savedLanguage);
      setOnboardingComplete(true);
    }
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLanguageSelect = (languageId) => {
    setSelectedLanguage(languageId);
    localStorage.setItem('ai_language', languageId);
    setOnboardingComplete(true);

    if (messages.length === 0) {
      const greetings = {
        english: "Hello! I'm your AgroAssist AI, your agricultural companion. How can I help you today?",
        french: "Bonjour ! Je suis votre AgroAssist AI, votre compagnon agricole. Comment puis-je vous aider aujourd'hui ?",
        igbo: "Ndewo! Abụ m AgroAssist AI gị, onye enyemaka ọrụ ugbo gị. Kedu otu m ga-esi nyere gị aka taa?",
        hausa: "Sannu! Ni ne AgroAssist AI, abokin aikin gonarku. Ta yaya zan iya taimaka muku yau?",
        yoruba: "Bawo ni! Emi ni AgroAssist AI rẹ, alabaṣepọ iṣẹ-ogbin rẹ. Bawo ni mo ṣe le ran ọ lọwọ loni?",
        pidgin: "How far! I be your AgroAssist AI, your farming companion. How I fit help you today?"
      };

      setMessages([{
        id: Date.now(),
        role: 'assistant',
        message: greetings[languageId] || greetings.english,
        created_at: new Date().toISOString()
      }]);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !imageFile) || isLoading) return;

    const userMessageContent = newMessage;
    const userImagePreview = imagePreview;
    const tempId = Date.now();

    setMessages(prev => [...prev, {
      id: tempId,
      role: 'user',
      message: userMessageContent,
      image_path: userImagePreview,
      created_at: new Date().toISOString(),
      isTemp: true
    }]);

    setNewMessage('');
    setImageFile(null);
    setImagePreview(null);
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('message', userMessageContent);
      formData.append('language', selectedLanguage);
      if (imageFile) {
        formData.append('image', imageFile);
      }
      if (activeSessionId) {
        formData.append('session_id', activeSessionId);
      }

      const response = await authAPI.sendAiMessage(formData);

      if (response.success) {
        const { session_id, user_message, ai_response } = response.data;

        if (!activeSessionId) {
          setActiveSessionId(session_id);
        }

        setMessages(prev => {
          const filtered = prev.filter(m => m.id !== tempId);
          return [
            ...filtered,
            { ...user_message, role: 'user' },
            { ...ai_response, role: 'assistant' }
          ];
        });
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        message: 'Sorry, I encountered an error. Please try again.',
        created_at: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (isInitialLoading) {
    return (
      <div className="ai-chat-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="thinking-indicator">
          <div className="thinking-dots">
            <span></span><span></span><span></span>
          </div>
          <p className="thinking-text">Loading AgroAssist History...</p>
        </div>
      </div>
    );
  }

  if (!onboardingComplete && messages.length === 0) {
    return (
      <div className="ai-onboarding-container">
        <div className="ai-onboarding-content">
          <div className="ai-avatar-container">
            <img src={aiAvatar} alt="AI Assistant" className="ai-avatar-large" />
          </div>
          <h2>Welcome to AgroAssist AI</h2>
          <p>Select your preferred language to continue:</p>
          <div className="language-selection">
            {languages.map(lang => (
              <button
                key={lang.id}
                className="language-button"
                onClick={() => handleLanguageSelect(lang.id)}
              >
                {lang.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-chat-container">
      <div className="ai-chat-header">
        <div className="header-left">
          <Link to="/dashboard" className="ai-back-button">
            <ArrowLeft size={20} />
          </Link>
          <div className="ai-info">
            <img src={aiAvatar} alt="AI Assistant" className="ai-avatar" />
            <div className="ai-details">
              <div className="ai-name">AgroAssist AI</div>
              <div className="ai-language">
                {languages.find(l => l.id === selectedLanguage)?.name}
              </div>
            </div>
          </div>
        </div>
        <div className="header-actions">
          <select
            className="language-selector"
            value={selectedLanguage}
            onChange={(e) => {
              const lang = e.target.value;
              setSelectedLanguage(lang);
              localStorage.setItem('ai_language', lang);
            }}
          >
            {languages.map(lang => (
              <option key={lang.id} value={lang.id}>{lang.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
        <div className="ai-chat-messages" style={{ flex: 1 }}>
          {messages.length === 0 && !isLoading && (
            <div className="empty-chat-placeholder">
              <div className="example-questions">
                <h3>Try asking me about:</h3>
                <div className="question-chips">
                  <button onClick={() => setNewMessage("How do I treat tomato blight disease?")}>Crop diseases</button>
                  <button onClick={() => setNewMessage("What's the best spacing for maize planting?")}>Planting techniques</button>
                  <button onClick={() => setNewMessage("How can I improve my soil fertility naturally?")}>Soil management</button>
                </div>
              </div>
            </div>
          )}

          {messages.map((message, index) => (
            <div
              key={message.id || index}
              className={`message ${message.role === 'user' ? 'user-message' : 'ai-message'}`}
            >
              <div className="message-avatar">
                {message.role === 'user' ? (
                  <div className="user-icon-avatar">
                    <User size={18} />
                  </div>
                ) : (
                  <img src={aiAvatar} alt="AI" />
                )}
              </div>
              <div className="message-bubble">
                {message.role === 'user' && message.image_path && (
                  <div className="message-image-container">
                    <img src={message.image_path} alt="Uploaded" className="message-image" />
                  </div>
                )}
                <div className="message-content">
                  {message.role === 'assistant' ? (
                    <SimpleMarkdownDisplay content={message.message} />
                  ) : (
                    message.message
                  )}
                </div>
                <div className="message-time">
                  {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="message ai-message">
              <div className="message-avatar">
                <img src={aiAvatar} alt="AI" />
              </div>
              <div className="message-bubble loading">
                <div className="thinking-indicator">
                  <div className="thinking-dots">
                    <span></span><span></span><span></span>
                  </div>
                  <p className="thinking-text">Thinking...</p>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="ai-chat-input-wrapper">
        {imagePreview && (
          <div className="input-image-preview">
            <img src={imagePreview} alt="Preview" />
            <button className="remove-preview" onClick={() => {
              setImageFile(null);
              setImagePreview(null);
            }}>×</button>
          </div>
        )}

        <form className="ai-chat-input" onSubmit={handleSendMessage}>

          {/* Hidden: Gallery picker */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            onChange={handleImageUpload}
          />

          {/* Hidden: Camera capture */}
          <input
            type="file"
            ref={cameraInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            capture="environment"
            onChange={handleImageUpload}
          />

          {/* Gallery button */}
          <button
            type="button"
            className="attachment-button"
            title="Upload from gallery"
            onClick={() => fileInputRef.current.click()}
          >
            <Image size={20} />
          </button>

          {/* Camera button */}
          <button
            type="button"
            className="attachment-button"
            title="Take a photo"
            onClick={() => cameraInputRef.current.click()}
          >
            <Camera size={20} />
          </button>

          <input
            type="text"
            className="message-input"
            placeholder="Ask AgroAssist AI..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            disabled={isLoading}
            ref={messageInputRef}
          />

          <button
            type="submit"
            className={`send-button ${(!newMessage.trim() && !imageFile) || isLoading ? 'disabled' : ''}`}
            disabled={(!newMessage.trim() && !imageFile) || isLoading}
          >
            <Send size={20} />
          </button>
        </form>
      </div>
    </div>
  );
}